import React from "react";
import DialogModal from "../../../../atoms/dialog";
import { 
  Typography, 
  Box, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  Chip, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Component to render object values recursively
const ObjectRenderer = ({ data } : { data: any }) => {
  if (!data) return null;
  
  if (typeof data === "string" || typeof data === "number" || typeof data === "boolean") {
    return <Typography>{data.toString()}</Typography>;
  }
  
  if (Array.isArray(data)) {
    return (
      <List dense>
        {data.map((item, index) => (
          <ListItem key={index}>
            {typeof item === "object" ? (
              <ObjectRenderer data={item} />
            ) : (
              <ListItemText primary={item} />
            )}
          </ListItem>
        ))}
      </List>
    );
  }
  
  return (
    <Box sx={{ ml: 2 }}>
      {Object.entries(data).map(([key, value]) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:
          </Typography>
          {typeof value === "object" ? (
            <ObjectRenderer data={value} />
          ) : (
            <Typography variant="body2">{value?.toString()}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

function ViewClientDescription({ isOpen, handleClose, data }: { isOpen: boolean, handleClose: () => void, data: any }) {
  if (!data) return null;
  
  // Extract archetypes and client_description
  const archetypes = data.archetypes || [];
  const clientDescription = data.client_description || {};
  
  return (
    <DialogModal handleClose={handleClose} isOpen={isOpen} title="Client Description">
      <Box sx={{ p: 2 }}>
        {/* Archetypes Section */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Archetypes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {archetypes.length > 0 ? (
                archetypes.map((archetype: string, index: number) => (
                  <Chip 
                    key={index} 
                    label={archetype} 
                    color="primary" 
                    variant="outlined" 
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No archetypes available
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Client Description Section */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detailed Client Description
            </Typography>
            
            {Object.keys(clientDescription).length > 0 ? (
              <Box>
                {Object.entries(clientDescription).map(([key, value]) => (
                  <Accordion key={key} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ pl: 1 }}>
                        {typeof value === "object" ? (
                          <ObjectRenderer data={value} />
                        ) : (
                          <Typography>{value?.toString()}</Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No client description details available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </DialogModal>
  );
}

export default ViewClientDescription;